import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Container,
  Form,
  Button,
  Table,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import Dialog from "../components/Dialog";
import OutgoingDialog from "../components/OutgoingDialog";
import EditOutgoingDialog from "../components/EditOutgoingDialog";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import nodeApi from "../lib/api";

function Outgoing() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("og_ch_no"); // Default filter type
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);

  const challansPerPage = 10;
  const queryClient = useQueryClient();

  // Fetch outgoing challans using useQuery with filter_type and q
  const {
    data: outgoingData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["outgoing", filterType, searchTerm, startDate, endDate],
    queryFn: async () => {
      const params = {};
      if (filterType && searchTerm) {
        params.filter_type = filterType;
        params.q = searchTerm;
      }
      const res = await nodeApi.get("/outgoing", { params });
      return res.data;
    },
    onError: (err) => {
      toast.error(`Failed to fetch challans: ${err.message}`);
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [outgoingData, searchTerm, filterType, startDate, endDate]);

  // Update challan using useMutation
  const updateChallanMutation = useMutation({
    mutationFn: async ({ challanId, updatedData }) => {
      const res = await nodeApi.put(`/outgoing/${challanId}`, updatedData);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Challan updated successfully!");
      queryClient.invalidateQueries(["outgoing"]);
    },
    onError: (err) => {
      let errMsg = "Failed to update challan";
      try {
        errMsg = err.response?.data?.message || err.message;
      } catch {
        errMsg = err.message || "An unexpected error occurred";
      }
      toast.error(errMsg);
    },
  });

  // Apply date filtering on the frontend
  const filteredChallans = outgoingData.filter((challan) => {
    if (!startDate && !endDate) return true;
    const rawDate = challan.og_ch_date;
    if (!rawDate) return false;

    const challanDateStr = new Date(rawDate).toISOString().split("T")[0];
    const startStr = startDate
      ? new Date(startDate).toISOString().split("T")[0]
      : null;
    const endStr = endDate
      ? new Date(endDate).toISOString().split("T")[0]
      : null;

    if (startStr && challanDateStr < startStr) return false;
    if (endStr && challanDateStr > endStr) return false;
    return true;
  });

  // Sort by og_ch_date or most recent of updated_at/created_at
  const sortedChallans = [...filteredChallans].sort((a, b) => {
    if (startDate || endDate) {
      const dateA = new Date(a.og_ch_date);
      const dateB = new Date(b.og_ch_date);
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      return dateB - dateA;
    } else {
      const dateA = new Date(
        Math.max(new Date(a.updated_at || 0), new Date(a.created_at || 0))
      );
      const dateB = new Date(
        Math.max(new Date(b.updated_at || 0), new Date(b.created_at || 0))
      );
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      return dateB - dateA;
    }
  });

  const handleViewChallan = (challan) => {
    setSelectedChallan(challan);
    setShowDialog(true);
  };

  const handleEditChallan = (challan) => {
    setSelectedChallan(challan);
    setShowEditDialog(true);
  };

  // Pagination logic
  const indexOfLast = currentPage * challansPerPage;
  const indexOfFirst = indexOfLast - challansPerPage;
  const currentChallans = sortedChallans.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedChallans.length / challansPerPage);
  const pages = [];
  if (totalPages > 1) {
    pages.push(1);
    if (currentPage > 3) pages.push("dots-left");
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p > 1 && p < totalPages) pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push("dots-right");
    if (totalPages > 1) pages.push(totalPages);
  }

  return (
    <div className="d-flex flex-column vh-100">
      <Navbar />
      <div className="d-flex flex-grow-1 overflow-hidden">
        <Sidebar />
        <Container
          fluid
          className="flex-grow-1 d-flex flex-column p-4 overflow-hidden"
        >
          <h4 className="mb-3 fw-bold">Outgoing Challans</h4>

          {/* Search & Filter Controls */}
          <Row className="mb-3 align-items-end">
            <Col xs={4} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Filter By</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="og_ch_no">Challan Number</option>
                  <option value="og_source_name">Source Name</option>
                  <option value="og_destination_name">Destination Name</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={8} md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1">End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              {(startDate || endDate || searchTerm) && (
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSearchTerm("");
                    setFilterType("og_ch_no");
                  }}
                >
                  Clear
                </Button>
              )}
            </Col>
            <Col xs={6} md={1} className="ms-auto">
              <Button variant="primary" onClick={() => setShowAddDialog(true)}>
                + Add
              </Button>
            </Col>
          </Row>

           <style>
    {`
      
      .col-challan { width: 200px; }
      .col-source { width: 250px; }
      .col-destination { width: 250px; }
      .col-date { width: 180px; }
      .col-actions { width: 200px; }
    `}
  </style>


          {/* Table */}
          <div className=" flex-grow-1 overflow-auto">
            <Table bordered hover className="mb-0">
              <thead className="table-secondary sticky-top">
                <tr>
                  <th className="col-challan" >Outgoing Challan No</th>
                  <th className="col-source" >Source Name</th>
                  <th className="col-destination" >Destination Name</th>
                  <th className="col-date" >Date</th>
                  <th className="col-action" >Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentChallans.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {isError
                        ? "Error loading challans"
                        : "No matching challans found"}
                    </td>
                  </tr>
                ) : (
                  currentChallans.map((challan) => (
                    <tr key={challan._id}>
                      <td>{challan.og_ch_no}</td>
                      <td>{challan.og_source_name}</td>
                      <td>{challan.og_destination_name}</td>
                      <td>
                        {new Date(challan.og_ch_date).toLocaleDateString(
                          "en-GB"
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title="Edit"
                          onClick={() => handleEditChallan(challan)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          title="View Details"
                          onClick={() => handleViewChallan(challan)}
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              className="justify-content-center mt-2"
              style={{ position: "sticky", bottom: 0, zIndex: 10 }}
            >
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </Pagination.Prev>
              {pages.map((p, idx) => (
                <Pagination.Item
                  key={idx}
                  active={p === currentPage}
                  disabled={p === "dots-left" || p === "dots-right"}
                  onClick={() =>
                    p !== "dots-left" && p !== "dots-right" && setCurrentPage(p)
                  }
                >
                  {p === "dots-left" || p === "dots-right" ? "…" : p}
                </Pagination.Item>
              ))}
              
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          )}
        </Container>
      </div>

      {/* View Dialog */}
      <Dialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        challan={selectedChallan}
        type="outgoing"
      />

      {/* Add Challan Modal */}
      <OutgoingDialog
        show={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />

      {/* Edit Challan Modal */}
      <EditOutgoingDialog
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        challan={selectedChallan}
        updateChallan={updateChallanMutation.mutate}
      />
    </div>
  );
}

export default Outgoing;
